import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useUI } from "../../Context/UIContext";
import TableLoadingOverlay from "../../Shared/TableLoadingOverlay";
import AdminTableScrollLayout from "../../Shared/AdminTableScrollLayout";
import AdminPageHeader from "../../Shared/AdminPageHeader";
import { adminDataTableHeadSx } from "../../../utils/adminTableScrollSx";
import { usePermissions } from "../../Context/permissionsProvider";
import useEditorViewportHeight from "../newsletter-editor/hooks/useEditorViewportHeight";
import {
  deleteBanner,
  listBanners,
  updateBanner,
  uploadBanner,
} from "../../../services/contentServices";
import {
  BANNER_IMAGE_ACCEPT,
  BANNER_IMAGE_UPLOAD_TOOLTIP,
  contentBannerGridSx,
  contentFieldSx,
  contentFillTableContainerSx,
  contentPageShellSx,
  contentPanelBodyShellSx,
  contentPanelFillInsetSx,
  contentPanelScrollInnerSx,
  contentPanelScrollSx,
  contentSectionMenubarSx,
  contentSectionMenubarTitleSx,
  contentSectionMenubarToolbarSx,
  contentViewportGridFillSx,
  contentViewportPanelSx,
  contentViewportSectionPaperSx,
} from "./contentEditorStyles";

const DEVICE_MODE_OPTIONS = [
  { value: "desktop", label: "Desktop" },
  { value: "mobile", label: "Mobile" },
];
const STATUS_OPTIONS = [
  { value: 1, label: "Attivo" },
  { value: 0, label: "Non Attivo" },
];

const emptyCreateDraft = () => ({
  pendingFile: null,
  previewUrl: "",
  posizione: 1,
  deviceMode: "desktop",
  isActive: 1,
});

const buildRowDrafts = (rows) =>
  rows.reduce((acc, banner) => {
    acc[banner.objectKey] = {
      posizione: banner.posizione,
      deviceMode: banner.deviceMode,
      isActive: banner.isActive,
    };
    return acc;
  }, {});

const isRowDirty = (banner, draft) =>
  Boolean(draft) && (
    draft.posizione !== banner.posizione
    || draft.deviceMode !== banner.deviceMode
    || draft.isActive !== banner.isActive
  );

const GestioneBannerContenuti = () => {
  const { isActionVisible } = usePermissions();
  const panelRef = useRef(null);
  const panelHeight = useEditorViewportHeight(panelRef);
  const { showError, showSuccess, showLoading, hideOverlay } = useUI();
  const [banners, setBanners] = useState([]);
  const [rowDrafts, setRowDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDraft, setCreateDraft] = useState(emptyCreateDraft);
  const [creating, setCreating] = useState(false);
  const [applyingKey, setApplyingKey] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);

  const dirtyRows = useMemo(
    () => banners.filter((banner) => isRowDirty(banner, rowDrafts[banner.objectKey])),
    [banners, rowDrafts]
  );

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listBanners();
      setBanners(rows);
      setRowDrafts(buildRowDrafts(rows));
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleCreateUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setCreateDraft((prev) => ({
      ...prev,
      pendingFile: file,
      previewUrl: URL.createObjectURL(file),
    }));
  };

  const handleCreate = async () => {
    if (!createDraft.pendingFile) {
      showError("Carica un'immagine per il nuovo banner.");
      return;
    }
    setCreating(true);
    showLoading("Pubblicazione sul sito…");
    try {
      await uploadBanner(createDraft.pendingFile, {
        posizione: createDraft.posizione,
        deviceMode: createDraft.deviceMode,
        isActive: createDraft.isActive,
      });
      showSuccess("Banner caricato e sito aggiornato.");
      setCreateDraft(emptyCreateDraft());
      await fetchBanners();
    } catch (error) {
      showError(error.message || "Impossibile pubblicare il banner sul sito.");
    } finally {
      hideOverlay();
      setCreating(false);
    }
  };

  const updateRowDraft = (objectKey, patch) => {
    setRowDrafts((prev) => ({
      ...prev,
      [objectKey]: {
        ...prev[objectKey],
        ...patch,
      },
    }));
  };

  const applyRowChanges = async (banner) => {
    const draft = rowDrafts[banner.objectKey];
    if (!isRowDirty(banner, draft)) {
      showError("Nessuna modifica da applicare per questo banner.");
      return;
    }
    setApplyingKey(banner.objectKey);
    showLoading("Aggiornamento sito…");
    try {
      await updateBanner({
        objectKey: banner.objectKey,
        posizione: draft.posizione,
        deviceMode: draft.deviceMode,
        isActive: draft.isActive,
      });
      showSuccess("Banner aggiornato sul sito.");
      await fetchBanners();
    } catch (error) {
      showError(error.message || "Impossibile aggiornare il sito.");
    } finally {
      hideOverlay();
      setApplyingKey(null);
    }
  };

  const applyAllChanges = async () => {
    if (dirtyRows.length === 0) {
      showError("Nessuna modifica da applicare.");
      return;
    }
    setApplyingAll(true);
    showLoading("Aggiornamento sito…");
    try {
      for (const banner of dirtyRows) {
        const draft = rowDrafts[banner.objectKey];
        await updateBanner({
          objectKey: banner.objectKey,
          posizione: draft.posizione,
          deviceMode: draft.deviceMode,
          isActive: draft.isActive,
        });
      }
      showSuccess("Modifiche pubblicate sul sito.");
      await fetchBanners();
    } catch (error) {
      showError(error.message || "Impossibile aggiornare il sito.");
    } finally {
      hideOverlay();
      setApplyingAll(false);
    }
  };

  const handleDelete = async (banner) => {
    showLoading("Aggiornamento sito…");
    try {
      await deleteBanner({ objectKey: banner.objectKey });
      showSuccess("Banner eliminato dal sito.");
      await fetchBanners();
    } catch (error) {
      showError(error.message || "Impossibile aggiornare il sito.");
    } finally {
      hideOverlay();
    }
  };

  return (
    <Box sx={{ ...contentPageShellSx, position: "relative" }}>
      <TableLoadingOverlay loading={loading} />
      <AdminPageHeader
        title="Gestione Banners Homepage"
        subtitle="Carica e ordina i banner per la homepage (desktop e mobile)."
      />

      <Box ref={panelRef} sx={contentViewportPanelSx(panelHeight)}>
        <Box sx={{ ...contentBannerGridSx, ...contentViewportGridFillSx }}>
          <Paper elevation={0} sx={contentViewportSectionPaperSx}>
            <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
              <Toolbar sx={contentSectionMenubarToolbarSx}>
                <Typography component="h2" sx={contentSectionMenubarTitleSx}>
                  Nuovo banner
                </Typography>
              </Toolbar>
            </AppBar>
            <Box sx={contentPanelBodyShellSx}>
              <Box sx={contentPanelScrollSx}>
                <Box sx={contentPanelScrollInnerSx}>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Posizione carousel"
                  value={createDraft.posizione}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setCreateDraft((prev) => ({
                      ...prev,
                      posizione: Number.isFinite(nextValue) && nextValue >= 1 ? Math.floor(nextValue) : 1,
                    }));
                  }}
                  inputProps={{ min: 1, step: 1 }}
                  sx={contentFieldSx}
                />
                <FormControl fullWidth size="small" sx={contentFieldSx}>
                  <InputLabel>Modalità</InputLabel>
                  <Select
                    label="Modalità"
                    value={createDraft.deviceMode}
                    onChange={(event) => setCreateDraft((prev) => ({ ...prev, deviceMode: event.target.value }))}
                  >
                    {DEVICE_MODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={contentFieldSx}>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    label="Stato"
                    value={createDraft.isActive}
                    onChange={(event) => setCreateDraft((prev) => ({ ...prev, isActive: Number(event.target.value) }))}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={contentFieldSx}>
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 72,
                      height: 180,
                      mt: 0.5,
                      p: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #e8edf3",
                      borderRadius: 1.5,
                      backgroundColor: "#fafbfc",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {createDraft.previewUrl ? (
                      <Box
                        component="img"
                        src={createDraft.previewUrl}
                        alt="Anteprima nuovo banner"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1.5,
                          border: "none",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Typography
                        color="text.secondary"
                        sx={{
                          fontSize: 14,
                          opacity: 0.65,
                        }}
                      >
                        Carica un'immagine per il nuovo banner.
                      </Typography>
                    )}
                  </Box>
                </FormControl>
          
                <Stack direction="row" spacing={0.5} sx={{ width: "100%" }}>
                  <Tooltip title={BANNER_IMAGE_UPLOAD_TOOLTIP} sx={{ flex: 1 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      sx={{ width: "100%", flex: 1 }}
                    >
                      {createDraft.pendingFile ? "CAMBIA" : "CARICA"}
                      <input hidden type="file" accept={BANNER_IMAGE_ACCEPT} onChange={handleCreateUpload} />
                    </Button>
                  </Tooltip>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleCreate}
                    disabled={creating}
                    sx={{ bgcolor: "#0b3d87", width: "100%", flex: 1 }}
                  >
                    SALVA
                  </Button>
                </Stack>
           
           
              </Stack>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} sx={contentViewportSectionPaperSx}>
            <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
              <Toolbar sx={contentSectionMenubarToolbarSx}>
                <Typography component="h2" sx={contentSectionMenubarTitleSx}>
                  Banner esistenti
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {dirtyRows.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {dirtyRows.length} modifica/e in sospeso
                  </Typography>
                ) : null}
              </Toolbar>
            </AppBar>
            <Box sx={contentPanelFillInsetSx}>
              <AdminTableScrollLayout
              containerSx={contentFillTableContainerSx(loading)}
              tableMinWidth={860}
              head={
                <TableHead sx={adminDataTableHeadSx}>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Anteprima</TableCell>
                    <TableCell sx={{ py: 1 }}>Posizione</TableCell>
                    <TableCell sx={{ py: 1 }}>Modalità</TableCell>
                    <TableCell sx={{ py: 1 }}>Stato</TableCell>
                    <TableCell align="right" sx={{ py: 1, width: 96 }}>
                      <Tooltip title="Salva tutte le modifiche">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={applyAllChanges}
                            sx={{ minWidth: 0, px: 1, mr: 0.5, bgcolor: (dirtyRows.length === 0 || applyingAll) ? 'grey.400' : '#1976d2' }}
                          >
                            <SaveIcon fontSize="small" />
                          </Button>
                    
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
              }
              body={
                <TableBody>
                  {!loading && banners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun banner configurato.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    banners.map((banner, index) => {
                      const draft = rowDrafts[banner.objectKey] || {
                        posizione: banner.posizione,
                        deviceMode: banner.deviceMode,
                        isActive: banner.isActive,
                      };
                      const dirty = isRowDirty(banner, draft);
                      return (
                        <TableRow
                          key={banner.objectKey}
                          sx={{
                            backgroundColor: dirty
                              ? "#fffbeb"
                              : index % 2 === 0
                                ? "white"
                                : "#f8fafc",
                          }}
                        >
                          <TableCell sx={{ py: 0.75 }}>
                            <Box
                              component="img"
                              src={banner.publicUrl}
                              alt={`Banner ${draft.deviceMode} posizione ${draft.posizione}`}
                              sx={{ width: 140, maxHeight: 72, objectFit: "cover", borderRadius: 1.5, border: "1px solid #e8edf3" }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={draft.posizione}
                              onChange={(event) => {
                                const nextValue = Number(event.target.value);
                                updateRowDraft(banner.objectKey, {
                                  posizione: Number.isFinite(nextValue) && nextValue >= 1 ? Math.floor(nextValue) : 1,
                                });
                              }}
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 88 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Select
                              size="small"
                              value={draft.deviceMode}
                              onChange={(event) => updateRowDraft(banner.objectKey, { deviceMode: event.target.value })}
                              sx={{ minWidth: 110 }}
                            >
                              {DEVICE_MODE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Select
                              size="small"
                              value={draft.isActive}
                              onChange={(event) => updateRowDraft(banner.objectKey, { isActive: Number(event.target.value) })}
                              sx={{ minWidth: 110 }}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.25 }}>
                            <Tooltip title="Salva">
                                <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                disabled={!dirty || applyingKey === banner.objectKey || applyingAll}
                                onClick={() => applyRowChanges(banner)}
                                sx={{ minWidth: 0, px: 1, mr: 0.5 }}
                                >
                                <SaveIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                            {isActionVisible("gestioneBanner", "elimina") && (
                              <Tooltip title="Elimina banner">
                                  <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => handleDelete(banner)}
                                  sx={{ minWidth: 0, px: 1 }}
                                  >
                                <DeleteIcon fontSize="small" />
                              </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              }
            />
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default GestioneBannerContenuti;
